import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy, query, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Notification } from '@/types';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Plus, Edit, Trash2, Bell } from 'lucide-react';
import { format } from 'date-fns';

export const AdminNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  
  // フォームデータ
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'リリース' as Notification['category'],
    type: 'info' as Notification['type'],
    showDate: true,
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(notificationsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const notificationsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Notification[];
      
      setNotifications(notificationsData);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      alert('お知らせの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (notification?: Notification) => {
    if (notification) {
      setEditingNotification(notification);
      setFormData({
        title: notification.title,
        content: notification.content || notification.message || '',
        category: notification.category || 'リリース',
        type: notification.type,
        showDate: notification.showDate !== false,
      });
    } else {
      setEditingNotification(null);
      setFormData({
        title: '',
        content: '',
        category: 'リリース',
        type: 'info',
        showDate: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingNotification(null);
    setFormData({
      title: '',
      content: '',
      category: 'リリース',
      type: 'info',
      showDate: true,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingNotification) {
        // 更新
        const notificationRef = doc(db, 'notifications', editingNotification.id);
        await updateDoc(notificationRef, {
          title: formData.title,
          message: formData.content,
          content: formData.content,
          category: formData.category,
          type: formData.type,
          showDate: formData.showDate,
          updatedAt: Timestamp.now(),
        });
        alert('お知らせを更新しました。');
      } else {
        // 新規追加
        await addDoc(collection(db, 'notifications'), {
          title: formData.title,
          message: formData.content,
          content: formData.content,
          category: formData.category,
          type: formData.type,
          targetUsers: 'all',
          readUsers: [],
          read: false,
          showDate: formData.showDate,
          createdAt: Timestamp.now(),
        });
        alert('お知らせを投稿しました。');
      }
      
      handleCloseModal();
      fetchNotifications();
    } catch (error) {
      console.error('Failed to save notification:', error);
      alert('お知らせの保存に失敗しました。');
    }
  };

  const handleDelete = async (notificationId: string) => {
    if (!confirm('このお知らせを削除しますか？')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
      alert('お知らせを削除しました。');
      fetchNotifications();
    } catch (error) {
      console.error('Failed to delete notification:', error);
      alert('お知らせの削除に失敗しました。');
    }
  };

  const getCategoryColor = (category: Notification['category']) => {
    switch (category) {
      case 'リリース':
        return 'bg-blue-100 text-blue-800';
      case 'メンテナンス':
        return 'bg-orange-100 text-orange-800';
      case 'サポート':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* ページヘッダー */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">お知らせ管理</h1>
            <p className="text-gray-600 mt-1">全ユーザーへのお知らせを管理</p>
          </div>
          
          <button
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            新規投稿
          </button>
        </div>

        {/* お知らせリスト */}
        <div className="card">
          <h2 className="text-lg font-bold text-gray-900 mb-4">投稿済みのお知らせ</h2>
          
          {notifications.length === 0 ? (
            <div className="h-96 flex items-center justify-center bg-gray-50 rounded-lg">
              <div className="text-center">
                <Bell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">お知らせはまだありません</p>
                <p className="text-sm text-gray-400 mt-1">
                  「新規投稿」ボタンからお知らせを作成できます
                </p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3">投稿日時</th>
                    <th scope="col" className="px-6 py-3">カテゴリー</th>
                    <th scope="col" className="px-6 py-3">タイトル</th>
                    <th scope="col" className="px-6 py-3 text-right">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((notification) => (
                    <tr key={notification.id} className="bg-white border-b hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(notification.createdAt.toDate(), 'yyyy/MM/dd HH:mm')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(notification.category)}`}>
                          {notification.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {notification.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleOpenModal(notification)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          <Edit className="w-4 h-4 inline" />
                        </button>
                        <button
                          onClick={() => handleDelete(notification.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* モーダル */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {editingNotification ? 'お知らせを編集' : '新規お知らせ'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    タイトル
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="input w-full"
                    placeholder="例: 新機能リリースのお知らせ"
                  />
                </div>
                
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    カテゴリー
                  </label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as Notification['category'] })}
                    className="input w-full"
                  >
                    <option value="リリース">リリース</option>
                    <option value="メンテナンス">メンテナンス</option>
                    <option value="サポート">サポート</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                    詳細
                  </label>
                  <textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    rows={6}
                    className="input w-full"
                    placeholder="お知らせの詳細を入力してください..."
                  />
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-800 font-medium mb-1">💡 リンクの書き方</p>
                    <p className="text-xs text-blue-700">
                      [リンクテキスト](URL) の形式でリンクを作成できます
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      例: [こちら](https://example.com) → 「こちら」がクリック可能になります
                    </p>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                    通知タイプ
                  </label>
                  <select
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as Notification['type'] })}
                    className="input w-full"
                  >
                    <option value="info">情報</option>
                    <option value="success">成功</option>
                    <option value="warning">警告</option>
                    <option value="error">エラー</option>
                  </select>
                </div>
                
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.showDate}
                      onChange={(e) => setFormData({ ...formData, showDate: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">投稿日時を表示する</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    チェックを外すと、「ようこそ」などの日付なし通知になります
                  </p>
                </div>
                
                <div className="flex items-center gap-3 pt-4">
                  <button type="submit" className="btn-primary flex-1">
                    {editingNotification ? '更新する' : '投稿する'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn-secondary flex-1"
                  >
                    キャンセル
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};
